import DictTable from '@/components/DictTable';
import Loader from '@/components/Loader';
import useKeyStats from '@/hooks/useKeyStats';
import MathUtils from '@/utils/MathUtils';
import StringUtils from '@/utils/StringUtils';
import clsx from 'clsx';
import { useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

interface IKeyStatsProps {
  className?: string;
  isEtf?: boolean;
  symbol: string;
}

function KeyStats({ className, isEtf, symbol }: IKeyStatsProps) {
  const { data: stats, isLoading } = useKeyStats(symbol!);
  const marketcap = stats?.profile?.mktCap;
  const week52high = stats?.metrics?.yearHigh;
  const week52low = stats?.metrics?.yearLow;
  const dividendYield = stats?.ratios?.[0]?.dividendYielTTM;
  const latestVolume = stats?.metrics?.volume;
  const avgTotalVolume = stats?.profile?.volAvg;
  const peRatio = stats?.ratios?.[0]?.peRatioTTM;
  const priceToSales = stats?.ratios?.[0]?.priceToSalesRatioTTM;

  const tableData = useMemo(() => {
    return (isEtf
    ? [[
      {
        key: '52 Week High',
        value: week52high ? StringUtils.USD(week52high as number) : '--',
        info: {
          title: '52 Week High',
          text: 'The highest price of a stock for the last 52 weeks.',
        },
      },
      {
        key: '52 Week Low',
        value: week52low ? StringUtils.USD(week52low as number) : '--',
        info: {
          title: '52 Week Low',
          text: 'The lowest price of a stock for the last 52 weeks.',
        },
      },
      {
        key: 'Dividend Yield',
        value: dividendYield
          ? `${MathUtils.round((dividendYield as number) * 100, 0.01)}%`
          : '--',
        info: {
          title: 'Dividend Yield',
          text: 'Amount of money a company pays investors for owning its stock yearly, divided by the company&apos;s current stock price. With dividends, investors can gain money without having to sell the stocks. Not all companies offer a "dividend". ',
        },
      },
      {
        key: "Today's Volume",
        value: latestVolume ? StringUtils.truncate(latestVolume as number) : '--',
        info: {
          title: 'Today’s Volume',
          text: 'Total number of shares of this stock bought and sold today.',
        },
      },
      {
        key: 'Avg Daily Volume',
        value: avgTotalVolume ? StringUtils.truncate(avgTotalVolume as number) : '--',
        info: {
          title: 'Average Daily Volume',
          text: 'Average number of shares of this stock bought and sold daily.',
        },
      },
    ]]
    : [[
      {
        key: 'Market Cap',
        value: marketcap ? `$${StringUtils.truncate(marketcap as number)}` : '--',
        info: {
          title: 'Market Cap',
          text: "The total value of all of a company&apos;s shares.",
        },
      },
      {
        key: 'Net Income (12 Months)',
        value: !!marketcap && !!peRatio ? `${peRatio < 0 ? '-' : ''}$${
          StringUtils.truncate(
            Math.round(
              Math.abs(marketcap / peRatio),
            ),
          )
        }` : '--',
        info: {
          title: 'Net Income (12 Months)',
          text: 'The amount of money a company made in the last 12 months after subtracting all of its expenses, such as salaries, rent, and taxes, from its total revenue.\n\nThis is sometimes know as a company&apos;s "real" profit. If this number is negaitve, it means the company lost money, and is unprofitable.',
        },
      },
      {
        key: 'Price/Earnings Ratio',
        value: peRatio ? MathUtils.round(peRatio as number, 0.01) : '--',
        info: {
          title: 'Price/Earnings Ratio',
          text: "The cost of a company&apos;s stock divided by its earnings per share (EPS). ",
        },
      },
      {
        key: 'Price/Revenue Ratio',
        value: priceToSales ? MathUtils.round(priceToSales as number, 0.01) : '--',
        info: {
          title: 'Price/Revenue Ratio',
          text: "Market cap divided by the company&apos;s revenue in the past 12 months.",
        },
      },
      {
        key: 'Dividend Yield',
        value: dividendYield
          ? `${MathUtils.round((dividendYield as number) * 100, 0.01)}%`
          : '--',
        info: {
          title: 'Dividend Yield',
          text: 'Amount of money a company pays investors for owning its stock yearly, divided by the company&apos;s current stock price. With dividends, investors can gain money without having to sell the stocks. Not all companies offer a "dividend". ',
        },
      }],[
      {
        key: '52 Week High',
        value: week52high ? StringUtils.USD(week52high as number) : '--',
        info: {
          title: '52 Week High',
          text: 'The highest price of a stock for the last 52 weeks.',
        },
      },
      {
        key: '52 Week Low',
        value: week52low ? StringUtils.USD(week52low as number) : '--',
        info: {
          title: '52 Week Low',
          text: 'The lowest price of a stock for the last 52 weeks.',
        },
      },
      {
        key: "Today's Volume",
        value: latestVolume ? StringUtils.truncate(latestVolume as number) : '--',
        info: {
          title: 'Today’s Volume',
          text: 'Total number of shares of this stock bought and sold today.',
        },
      },
      {
        key: 'Avg Daily Volume',
        value: avgTotalVolume ? StringUtils.truncate(avgTotalVolume as number) : '--',
        info: {
          title: 'Average Daily Volume',
          text: 'Average number of shares of this stock bought and sold daily.',
        },
      },
    ]])
  }, [
    isEtf,
    avgTotalVolume,
    dividendYield,
    latestVolume,
    marketcap,
    peRatio,
    priceToSales,
    week52high,
    week52low,
  ]);

  return (
    <div className={twMerge(clsx('rounded-2xl bg-darkglass p-6', className))}>
      <h4 className="mb-2">Key stats</h4>
      {!isLoading ? <DictTable columns={tableData} /> : <Loader className="mx-auto" />}
    </div>
  );
}

export default KeyStats;
